<template>
  <Command
    v-if="!wpsClient.interactive && !wpsClient.media.isMediaOverlay"
    @click="trigger"
  >
    <slot
      v-bind="{
        disabled,
        icon: 'text_to_speech',
        label: t('moreOptions.readAloud'),
        toolTip: t('tts.iconTooltip'),
      }"
    />
  </Command>
</template>
<script setup lang="ts">
import { computed, ref, toValue } from "vue";
import { useI18n } from "vue-i18n";

import { Locator } from "@readium/shared";

import { useNavigator } from "@/components/navigator";
import { posthogKeys } from "@/helpers/constants";
import { usePostHog } from "@/helpers/posthog";
import TTSEngine from "@/services/tts/TTSEngine";

import Command from "../command";
import { useWpsClient } from "../../wps/client";

const props = defineProps<{
  startAt?: Locator;
}>();

const { capture: capturePosthogEvent } = usePostHog();

const { t } = useI18n();

const wpsClient = useWpsClient();

const navigator = useNavigator();

const isStarting = ref(false);

const disabled = computed(() => !window.speechSynthesis || isStarting.value);

async function trigger() {
  if (!window.speechSynthesis || isStarting.value) {
    return;
  }
  try {
    isStarting.value = true;

    // call prime while we're still in the event's context / call stack,
    // to avoid potential issues with speechSynthesis being unavailable after an async gap
    await TTSEngine.prime();

    const utterance = await toValue(navigator).getUtterance(props.startAt);
    if (utterance) {
      capturePosthogEvent(posthogKeys.tts);
      await TTSEngine.start(utterance);
    }
  } finally {
    isStarting.value = false;
  }
}
</script>
